import { useState, useMemo, useCallback, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Loader2, Users, X } from "lucide-react";
import { useDeleteUser, useInfiniteUsers } from "@/domains/users/services/users";
import ContainerLayout from '@/shared/components/ContainerLayout';
import EmptyView from "@/shared/components/EmptyView";
import { User } from "@/shared/types/userTypes";
import GenericAlertDialog from "@/shared/components/GenericAlertDialog";
import { showSuccess } from "@/shared/utils/toast-utils";
import { showErrorWithApiError } from "@/shared/utils/toast-utils";
import { ERROR } from "@/config/toast-message-config";
import { SUCCESS } from "@/config/toast-message-config";
import SearchBar from "@/shared/components/SearchBar";
import UserCard from "@/domains/users/components/UserCard";
import { Button } from "@/shared/ui/button";
import ManageGlobalOrganizationUserDialog from "@/domains/users/dialogs/ManageGlobalOrganizationUserDialog";
import { Separator } from '@/shared/ui/separator';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/shared/ui/select";
import { OrgType } from "@/shared/types/userRoleTypes";
import { Feature, Permission } from "@/shared/types/permissionsTypes";
import { usePermissions } from "@/shared/hooks/use-permissions";

enum UserType {
  PLATFORM = 'Platform',
  TENANT = 'Tenant',
  CUSTOMER = 'Customer',
}

const UsersPage = () => {
  const { hasPermission } = usePermissions();
  const canManage = hasPermission(OrgType.PLATFORM, Feature.USERS, Permission.MANAGE);

  const queryClient = useQueryClient();
  const {
    data: usersInfiniteData,
    isLoading: isLoadingUsers,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
    error: usersError,
    refetch: refetchUsers,
  } = useInfiniteUsers();
  const [userTypeFilter, setUserTypeFilter] = useState('all');
  const deleteUser = useDeleteUser();

  // Dialogs
  const [isDeleteUserDialogOpen, setIsDeleteUserDialogOpen] = useState(false);
  const [isManageUserDialogOpen, setIsManageUserDialogOpen] = useState(false);

  const selectedUser = useRef<User | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const users = useMemo(() => {
    const pages = usersInfiniteData?.pages ?? [];
    const seen = new Set<string>();
    const merged: User[] = [];
    for (const page of pages) {
      for (const u of page.results ?? []) {
        if (!seen.has(u.userId)) {
          seen.add(u.userId);
          merged.push(u);
        }
      }
    }
    return merged;
  }, [usersInfiniteData?.pages]);

  const totalCount = usersInfiniteData?.pages[0]?.totalCount ?? 0;

  const filteredUsers = useMemo(() => {
    if (!searchTerm && userTypeFilter === 'all') return users;
    if (userTypeFilter !== 'all') {
      return users.filter(user => {
        if (userTypeFilter === UserType.PLATFORM) {
          return (user.availableRegions?.length ?? 0) > 0;
        }
        if (userTypeFilter === UserType.TENANT) {
          return user.organizations?.some(org => org.organizationType === OrgType.TENANT);
        }
        return user.organizations?.some(org => org.organizationType === OrgType.CUSTOMER);
      });
    }

    const searchLower = searchTerm.toLowerCase();
    return users.filter(user =>
      user.userId.toLowerCase().includes(searchLower) ||
      user.name.toLowerCase().includes(searchLower) ||
      user.email.toLowerCase().includes(searchLower) ||
      user.organizations?.some(organization =>
        organization.organizationId.toLowerCase().includes(searchLower) ||
        organization.name.toLowerCase().includes(searchLower)
      )
    );
  }, [users, searchTerm, userTypeFilter]);

  const refetchUsersData = useCallback(() => {
    void queryClient.resetQueries({ queryKey: ['users-infinite'] });
  }, [queryClient]);

  const onManageUser = (user: User) => {
    if (!user) return;
    selectedUser.current = user;
    setIsManageUserDialogOpen(true);
  };

  const onDeleteUser = (user: User) => {
    if (!user) return;
    selectedUser.current = user;
    setIsDeleteUserDialogOpen(true);
  };

  const handleConfirmDeleteUser = async () => {
    if (!selectedUser) return;
    try {
      await deleteUser.mutateAsync({ userId: selectedUser.current.userId });
      showSuccess(SUCCESS.USER.DELETED);
      refetchUsersData();
    } catch (error) {
      showErrorWithApiError(error, ERROR.USER.DELETE);
    }
    setIsDeleteUserDialogOpen(false);
    selectedUser.current = null;
  };

  return (
    <ContainerLayout shouldShowSidebar={true} shouldAllowRegionChange={true} shouldAllowAccountChange={false}>
      <div className="flex flex-col flex-1 p-4 space-y-4">
        {/* Fixed Header Section */}
        <div className="p-4 flex items-center justify-between rounded-lg bg-muted">
          <div>
            <h1 className="text-3xl font-bold">Users</h1>
            <p className="text-lg">Manage users and their roles across all accounts</p>
          </div>
        </div>
        {/* Search Bar */}
        {users && users.length > 0 && (
          <div className="flex items-center gap-2 justify-between">
            <div className="relative w-full max-w-1/2">
              <SearchBar
                value={searchTerm}
                onChange={setSearchTerm}
                placeholder="Search users by ID, name, or email..."
              />
            </div>
            <div className="flex items-center gap-2">
              {/* User Type Filter */}
              <div className="flex items-center gap-2 min-w-64">
                <Select value={userTypeFilter} onValueChange={(value) => setUserTypeFilter(value as UserType)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Terminal Type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Types</SelectItem>
                    {Object.values(UserType).map((type) => (
                      <SelectItem key={type} value={type}>
                        {type}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Clear Filters Button */}
              <Button variant="outline" onClick={() => {
                setUserTypeFilter('all');
                setSearchTerm('');
              }}>
                <X className="h-4 w-4" />
                Clear Filters
              </Button>
            </div>
          </div>
        )}

        <Separator />

        {isLoadingUsers ? (
          <div className="flex items-center justify-center gap-2">
            <Loader2 className="h-8 w-8 animate-spin" />
            <p className="text-sm">Loading Users...</p>
          </div>
        ) : (
          usersError ? (
            <EmptyView
              icon={Users}
              title="Error Loading Users"
              description="Error loading users at the moment. Please try again."
              actionLabel="Retry"
              onAction={refetchUsers}
            />
          ) : (
            <>
              {/* Scrollable Content Section */}
              {filteredUsers && filteredUsers.length > 0 ? (
                <div className="flex-1 overflow-y-auto space-y-4">
                  {filteredUsers?.map((user) => (
                    <UserCard
                      key={user.userId}
                      user={user}
                      onManageUser={canManage ? () => onManageUser(user) : undefined}
                      onDeleteUser={canManage ? () => onDeleteUser(user) : undefined}
                    />
                  ))}

                  <div className="glass-panel flex flex-col gap-3 border-t p-4 sm:flex-row sm:items-center sm:justify-between rounded-lg">
                    <div className="flex min-w-0 flex-col gap-0.5 sm:flex-row sm:items-center sm:gap-2">
                      <span className="text-md">
                        Loaded <span className="font-medium">{users.length}</span>
                        {' '}of <span className="font-bold">{totalCount}</span> users
                      </span>
                      {filteredUsers.length !== users.length && (
                        <span className="text-sm">
                          ({filteredUsers.length} match current search query)
                        </span>
                      )}
                    </div>
                    {hasNextPage ? (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="shrink-0 gap-2 self-start sm:self-auto"
                        disabled={isFetchingNextPage}
                        onClick={() => void fetchNextPage()}
                      >
                        {isFetchingNextPage ? (
                          <>
                            <Loader2 className="h-4 w-4 animate-spin" />
                            Loading…
                          </>
                        ) : (
                          'Load more'
                        )}
                      </Button>
                    ) : null}
                  </div>
                </div>
              ) : (
                <EmptyView
                  icon={Users}
                  title={searchTerm ? "No Matching Users Found" : "No Users Found"}
                  description={searchTerm ? "Try adjusting your search criteria to find what you're looking for." : "There are no users available."}
                  actionLabel={searchTerm && hasNextPage ? "Load More" : undefined}
                  onAction={searchTerm && hasNextPage ? () => void fetchNextPage() : undefined}
                />
              )}
            </>
          )
        )}
      </div>

      {/* Manage User Dialog */}
      <ManageGlobalOrganizationUserDialog
        isOpen={isManageUserDialogOpen}
        onClose={() => {
          setIsManageUserDialogOpen(false);
          selectedUser.current = null;
          refetchUsers();
        }}
        user={selectedUser.current}
      />

      {/* Delete User Dialog */}
      <GenericAlertDialog
        open={isDeleteUserDialogOpen}
        onOpenChange={setIsDeleteUserDialogOpen}
        title="Delete User"
        description="Are you sure you want to delete this user?"
        confirmLabel="Delete"
        cancelLabel="Cancel"
        onConfirm={handleConfirmDeleteUser}
        loading={deleteUser.isPending}
      />

    </ContainerLayout>
  );
};

export default UsersPage; 